import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { IoChevronBackOutline, IoChevronForwardOutline, IoChevronDownOutline, IoChevronUpOutline, IoCloudUploadOutline } from 'react-icons/io5';
import axios from 'axios';
import Swal from 'sweetalert2';
import AccessDeniedState from '../../../components/AccessDeniedState';
import AccidentLocationMap from '../../../components/form/AccidentLocationMap';
import OpenFormDatePicker from '../../../components/form/OpenFormDatePicker';
import OpenFormDateTimeField from '../../../components/form/OpenFormDateTimeField';
import { getLoggedInUser, hasModulePermission } from '../../../utils/permissions';
import pointOfImpactDiagram from '../../../assets/DEF_Damage.png';
import { openDocumentViewer } from '../../../utils/documentViewer';

const MAX_UPLOAD_SIZE_BYTES = 1024 * 1024 * 1024;

const STEP_ITEMS = [
  'Getting Started',
  'Rental Agreement Details',
  'Accident Overview',
  'Vehicle Identification',
  'Incident Specifics',
  'Injury & Liability',
  'Documents & Photos',
];

const CLAIM_PRIORITY_OPTIONS = [
  '',
  'Moderate',
  'Escalated',
  'Other',
];

const CLAIM_SOURCE_OPTIONS = [
  '',
  'Walk-in return',
  'Roadside accident',
  'Police notification',
  'Customer self-report',
  'Telematics alert',
  'Third party insurance notification',
  'Attorney notification',
  'Other',
];

const DRIVER_SELECTION_OPTIONS = [
  '',
  'Driver',
  'Other',
];

const INSURANCE_TYPE_OPTIONS = [
  '',
  'Personal credit card',
  'Personal auto policy',
  'Online insurance',
  'Inclusive rate code',
  'Other',
];

const YES_NO_OPTIONS = [
  '',
  'Yes',
  'No',
];

const WEATHER_CONDITION_OPTIONS = [
  '',
  'Sunny',
  'Rain',
  'Snow',
  'Foggy',
  'Windy',
  'Hail',
  'Other',
];

const ROAD_CONDITION_OPTIONS = [
  '',
  'Dry',
  'Wet',
  'Icy',
  'Snow',
  'Gravel',
  'Mud',
  'Seaside',
  'Other',
];

const LDW_STATUS_OPTIONS = [
  '',
  'Accepted',
  'Declined',
  'Unknown',
];

const LDW_DEDUCTIBLE_AMOUNT_OPTIONS = [
  '',
  '0 to 500',
  '501 to 1000',
  '1001 to 2000',
  '2001 to 3000',
  '3001 to 4000',
  '4001 to 5000',
  'Other',
];

const VEHICLE_STATUS_OPTIONS = [
  '',
  'Drivable',
  'Non-Drivable',
  'Impounded',
  'Towed',
];

const VEHICLE_IDENTIFICATION_STATUS_OPTIONS = [
  '',
  'Drivable',
  'Non-Drivable',
  'Impounded',
  'Towed',
  'Other',
];

const TYPE_OF_LOSS_OPTIONS = [
  '',
  'Animal strike',
  'Flood/Water damage',
  'Hail',
  'Hit & run',
  'Multi-vehicle accident',
  'Theft/Stolen',
  'Vandalism',
  'Windshield/Glass damage',
  'Other',
];

const POINT_OF_IMPACT_POINTS = [
  { id: 'front-upper', label: 'Front Upper', x: 10, y: 35 },
  { id: 'front-middle', label: 'Front Middle', x: 10, y: 49 },
  { id: 'front-lower', label: 'Front Lower', x: 10, y: 63 },
  { id: 'top-left-front', label: 'Passenger Side Front', x: 20, y: 23 },
  { id: 'top-left-wheel', label: 'Passenger Side Front Wheel', x: 32, y: 12 },
  { id: 'top-left-wheel-center', label: 'Passenger Side Front Center', x: 44, y: 12 },
  { id: 'top-center', label: 'Passenger Side Center', x: 55, y: 12 },
  { id: 'top-rear-wheel', label: 'Passenger Side Rear Wheel', x: 65, y: 12 },
  { id: 'top-rear', label: 'Passenger Side Rear', x: 79, y: 23 },
  { id: 'right-upper', label: 'Rear Upper', x: 90, y: 35 },
  { id: 'right-middle', label: 'Rear Middle', x: 90, y: 49 },
  { id: 'right-lower', label: 'Rear Lower', x: 90, y: 63 },
  { id: 'bottom-left-front', label: 'Driver Side Front', x: 20, y: 76 },
  { id: 'bottom-left-wheel', label: 'Driver Side Front Wheel', x: 32, y: 88 },
  { id: 'bottom-left-wheel-center', label: 'Driver Side Front Center', x: 44, y: 88 },
  { id: 'bottom-center', label: 'Driver Side Center', x: 56, y: 88 },
  { id: 'bottom-rear-wheel', label: 'Driver Side Rear Wheel', x: 65, y: 88 },
  { id: 'bottom-rear', label: 'Driver Side Rear', x: 79, y: 76 },
  { id: 'hood-left', label: 'Hood Front', x: 31, y: 49 },
  { id: 'hood-center', label: 'Front Windshield', x: 41, y: 49 },
  { id: 'hood-roof-center', label: 'Hood Roof Center', x: 53, y: 49 },
  { id: 'roof-center', label: 'Rear Windshield', x: 65, y: 49 },
  { id: 'trunk-center', label: 'Trunk Center', x: 75, y: 49 },
];

const DOCUMENT_REQUIRED_UPLOADS = [
  'Signed Rental Agreement',
  'Incident Report / Customer Statement',
  'AOB Form',
  'Damage Photos',
];

const DOCUMENT_SUGGESTIONS = [
  'Police Report',
  'Towing/Impound Invoices',
  'Repair Invoices',
  'Estimates',
  'Accident Scene Pictures',
];

const US_STATE_OPTIONS = [
  '',
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
];

const COUNTRY_OPTIONS = [
  'United States',
  'Canada',
];

const CANADA_PROVINCE_OPTIONS = [
  '',
  'Alberta',
  'British Columbia',
  'Manitoba',
  'New Brunswick',
  'Newfoundland and Labrador',
  'Northwest Territories',
  'Nova Scotia',
  'Nunavut',
  'Ontario',
  'Prince Edward Island',
  'Quebec',
  'Saskatchewan',
  'Yukon',
];

const STATE_OPTIONS = [...US_STATE_OPTIONS, ...CANADA_PROVINCE_OPTIONS.filter(Boolean)];

const getStateOptionsByCountry = (country) => (
  country === 'Canada' ? CANADA_PROVINCE_OPTIONS : US_STATE_OPTIONS
);

const getValidStateForCountry = (stateValue, country) => (
  getStateOptionsByCountry(country).includes(stateValue) ? stateValue : ''
);

const DOCUMENT_DEFINITIONS = [
  { title: 'Accident/Police Report', description: 'reports relating to an accident/incident that occurred.' },
  { title: 'Appraisal', description: 'appraisals/estimates related to an auto repair.' },
  { title: 'Expense Bill', description: 'Vendor bills/invoices.' },
  { title: 'First Notice of Loss', description: 'form/documents reporting a new loss.' },
  { title: 'General Correspondence', description: 'general correspondence/documents.' },
  { title: 'Legal Correspondence', description: 'correspondence from an attorney.' },
  { title: 'Medical Bill', description: 'bills from medical providers or hospitals.' },
  { title: 'Medical Record', description: 'records relating to a medical condition/office visit.' },
  { title: 'Photographs', description: 'photographs relating to the claim.' },
  { title: 'Suit', description: 'legal documents related to a lawsuit.' },
];

// const FRAUD_WARNING_TEXT = 'Any person who knowingly presents a false or fraudulent claim for payment of loss or benefit with the intent to defraud or deceive any insurer is guilty of a crime and may be subject to criminal and civil penalties and denial of insurance benefits. Any party completing and filing a claim using this form should be aware of state specific fraud warning statements, as applicable.';

const createPassengerForm = () => ({
  firstName: '',
  lastName: '',
  phone: '',
  extension: '',
  email: '',
  address: '',
  zipCode: '',
  city: '',
  state: '',
  country: 'United States',
  injuryDescription: '',
});

const createPedestrianForm = () => ({
  firstName: '',
  lastName: '',
  phone: '',
  extension: '',
  email: '',
  address: '',
  zipCode: '',
  city: '',
  state: '',
  country: 'United States',
  injuryDescription: '',
});

const createWitnessForm = () => ({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
});

const createVehicleForm = () => ({
  isCoveredUnderPolicy: '',
  unitFleetNumber: '',
  vin: '',
  year: '',
  make: '',
  model: '',
  color: '',
  licensePlate: '',
  stateIssued: '',
  vehicleStatus: '',
  vehicleStatusOther: '',
  currentVehicleLocation: '',
  odometerReading: '',
  typeOfLoss: '',
  typeOfLossOther: '',
  drivabilityStatus: '',
  towProviderInfo: '',
  storageYardInfo: '',
  policeAgencyCaseNumber: '',
  pointOfImpact: '',
  pointOfImpactPosition: '',
  pointOfImpactCode: '',
  pointOfImpactDetails: '',
  pointOfImpactSelections: [],
  wasVehicleDamaged: '',
  driverFirstName: '',
  driverLastName: '',
  driverPhone: '',
  driverExtension: '',
  driverEmail: '',
  driverAddress: '',
  driverZipCode: '',
  driverCity: '',
  driverState: '',
  driverCountry: 'United States',
  driverLicenseNumber: '',
  driverLicenseState: '',
  driverDateOfBirth: '',
  isDriverAlsoOwner: '',
  ownerFirstName: '',
  ownerLastName: '',
  ownerPhone: '',
  ownerExtension: '',
  ownerEmail: '',
  ownerAddress: '',
  ownerZipCode: '',
  ownerCity: '',
  ownerState: '',
  ownerCountry: 'United States',
  wasDriverInjured: '',
  driverInjuryDescription: '',
  wasOtherDriverInjured: '',
  otherDriverInjuryDescription: '',
  werePassengersInjured: '',
  passengers: [createPassengerForm()],
  wasPersonalPropertyDamaged: '',
  personalPropertyDescription: '',
  personalPropertyEstimatedDamage: '',
  wereAirbagsDeployed: '',
  isVehicleDrivable: '',
  vehicleLocation: 'With Insured',
  vehicleDamageDescription: '',
});

const ADDITIONAL_INFORMATION_RADIO_OPTIONS = ['Yes', 'No', "Don't Know"];

const createReportTimestamp = () => {
  const reportDate = new Date();
  const pad = (value) => String(value).padStart(2, '0');

  const day = pad(reportDate.getDate());
  const month = pad(reportDate.getMonth() + 1);
  const year = reportDate.getFullYear();

  let hours = reportDate.getHours();
  const minutes = pad(reportDate.getMinutes());
  const seconds = pad(reportDate.getSeconds());
  const meridiem = hours >= 12 ? 'PM' : 'AM';

  hours = hours % 12 || 12;

  return `${day}-${month}-${year} ${pad(hours)}:${minutes}:${seconds} ${meridiem}`;
};

const getTodayDateInputValue = () => {
  const today = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  return `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
};

const getTodayDateTimeInputValue = () => {
  const now = new Date();
  const pad = (value) => String(value).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
};

const normalizeUserLocations = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  if (typeof value === 'string') {
    return value.split(',').map((item) => item.trim()).filter(Boolean);
  }

  return [];
};

const createInitialForm = (currentUser = {}) => ({
  seriousInjury: '',
  reportingEmployeeName: currentUser?.userName || '',
  reportingEmployeeId: currentUser?.userUniqueId || currentUser?.user_unique_id || '',
  branchLocationCode: '',
  reportDateTime: createReportTimestamp(),
  reportDateTimeTimezone: '',
  customerReportedDateTime: '',
  customerReportedDateTimeTimezone: '',
  claimPriorityLevel: '',
  claimPriorityLevelOther: '',
  claimSource: '',
  claimSourceOther: '',
  rentalAgreementNumber: '',
  tireGlassWaiver: '',
  authorizedDriverName: '',
  driverAtTimeOfAccident: '',
  driverAtTimeOfAccidentOther: '',
  customerPhone: '',
  preAuthorizationAmount: '',
  ldwStatus: '',
  ldwDeductibleAmount: '',
  ldwDeductibleAmountOther: '',
  customerInsuranceCarrier: '',
  insuranceType: '',
  insuranceTypeOther: '',
  customerPolicyNumber: '',
  claimOwningBranch: '',
  rentalStartDate: '',
  rentalReturnDate: '',
  originalRentalReturnDate: '',
  relationshipToClaim: '',
  relationshipOther: '',
  email: '',
  cellPhone: '',
  dealershipClaim: '',
  insuredCompanyName: '',
  parentCompanyName: '',
  routesPolicyNumber: '',
  siteCode: '',
  insuredCompanyAddress: '',
  zipCode: '',
  city: '',
  state: '',
  country: 'United States',
  insuredCompanyPhone: '',
  insuredCompanyExtension: '',
  primaryInsuredContact: '',
  primaryContactName: '',
  primaryContactEmail: '',
  primaryContactPhone: '',
  primaryContactExtension: '',
  primaryContactCellPhone: '',
  sameAsCellPhone: false,
  accidentDateTime: '',
  accidentDateTimeTimezone: '',
  accidentDate: '',
  accidentTime: '',
  sameAsInsuredLocation: false,
  accidentLocation: '',
  gpsCoordinates: '',
  gpsLatitude: '',
  gpsLongitude: '',
  accidentAddress: '',
  accidentZipCode: '',
  accidentCity: '',
  accidentState: '',
  accidentCountry: 'United States',
  accidentDescription: '',
  reportedToAuthorities: '',
  weatherConditions: '',
  weatherConditionsOther: '',
  roadConditions: '',
  roadConditionsOther: '',
  policeContacted: true,
  policeName: '',
  policeReportNumber: '',
  fireDepartmentContacted: true,
  fireDepartmentName: '',
  fireDepartmentReportNumber: '',
  ambulanceContacted: true,
  ambulanceName: '',
  ambulanceReportNumber: '',
  otherAuthorityContacted: true,
  otherAuthorityName: '',
  otherAuthorityReportNumber: '',
  thirdPartyDriverName: '',
  thirdPartyPlateNumber: '',
  thirdPartyInsuranceDetails: '',
  werePedestriansInjured: '',
  pedestrians: [createPedestrianForm()],
  wereWitnessesPresent: '',
  witnesses: [createWitnessForm()],
  wasNonPersonalPropertyDamaged: '',
  isPropertyCoveredUnderRoutesPolicy: '',
  additionalInformationNotes: '',
  uploadedFiles: [],
  showDocumentDefinitions: false,
  receiveTextConfirmation: false,
});

const STEP_PLACEHOLDER_COPY = {
  1: 'Routes insured information fields will go here next.',
  2: 'Accident overview fields will go here next.',
  3: 'Vehicle detail fields will go here next.',
  4: 'Additional information fields will go here next.',
  5: 'Document and photo upload fields will go here next.',
};
const SUBMITTED_FORM_PERMISSION_KEYS = ['Submited Forms', 'Submitted Forms'];

const buildExistingUploadedFiles = (row) => (
  Array.isArray(row?.uploaded_files)
    ? row.uploaded_files.map((file, index) => ({
        id: `${file?.saved_name || file?.original_name || 'file'}-${index}`,
        original_name: file?.original_name || file?.saved_name || `File ${index + 1}`,
        saved_name: file?.saved_name || '',
        path: file?.path || file?.s3_key || file?.saved_name || '',
        s3_bucket: file?.s3_bucket || '',
        s3_key: file?.s3_key || '',
        content_type: file?.content_type || '',
      }))
    : []
);

const createEditForm = (row, currentUser = {}) => {
  const nextForm = createInitialForm(currentUser);
  const nestedKeys = new Set(['details_by_vehicle', 'pedestrians', 'witnesses', 'uploaded_files']);

  Object.entries(row || {}).forEach(([key, value]) => {
    if (nestedKeys.has(key) || key === '_id') {
      return;
    }

    if (key in nextForm) {
      nextForm[key] = value ?? nextForm[key];
    }
  });

  nextForm.uploadedFiles = [];
  nextForm.pedestrians = Array.isArray(row?.pedestrians) && row.pedestrians.length
    ? row.pedestrians.slice(0, 2).map((pedestrian) => ({ ...createPedestrianForm(), ...pedestrian }))
    : [createPedestrianForm()];
  nextForm.witnesses = Array.isArray(row?.witnesses) && row.witnesses.length
    ? row.witnesses.slice(0, 2).map((witness) => ({ ...createWitnessForm(), ...witness }))
    : [createWitnessForm()];

  return nextForm;
};

const createEditVehicles = (row) => (
  Array.isArray(row?.details_by_vehicle) && row.details_by_vehicle.length
    ? row.details_by_vehicle.slice(0, 1).map((vehicle) => ({
        ...createVehicleForm(),
        ...vehicle,
        passengers: Array.isArray(vehicle?.passengers) && vehicle.passengers.length
          ? vehicle.passengers.slice(0, 4).map((passenger) => ({ ...createPassengerForm(), ...passenger }))
          : [createPassengerForm()],
      }))
    : [createVehicleForm()]
);

const buildFileUrl = (baseURL, filePath = '', download = false) => {
  const normalizedBaseURL = String(baseURL || '').replace(/\/$/, '');
  const normalizedFilePath = String(filePath || '').replace(/\\/g, '/');
  const fileName = normalizedFilePath.split('/').filter(Boolean).pop();

  if (!normalizedBaseURL || !fileName) {
    return '';
  }

  const url = `${normalizedBaseURL}/open-form-upload/${encodeURIComponent(fileName)}`;
  return download ? `${url}?download=1` : url;
};

export default function EditSubmittedForm({
  initialRow = null,
  onCancel = null,
  onSaved = null,
  updateEndpointBase = '/update_submitted_open_form',
  promoteEndpointBase = '/promote_open_form_to_claim_processing',
  promoteToProcessingOnSubmit = false,
  finalSubmitLabel = 'Save Changes',
  submittedFormsPath = '/submitted-forms',
  permissionKeys = SUBMITTED_FORM_PERMISSION_KEYS,
  readOnly = false,
  workflowName = 'submitted insurance form',
  accessDeniedMessage = 'You do not have permission to view submitted form edits.',
  workflowTag = '',
  workflowVariant = 'standard',
}) {
  const isWindshieldTire = workflowVariant === 'windshield-tire';
  const navigate = useNavigate();
  const location = useLocation();
  const submittedRow = initialRow || location.state?.row || null;
  const [activeStep, setActiveStep] = useState(0);
  const currentUser = useMemo(() => getLoggedInUser(), []);
  const accessRole = currentUser?.accessRole || {};
  const submittedPermissionKeys = useMemo(() => {
    const specificKeys = permissionKeys.filter((key) => /submi(?:t|te)d forms/i.test(String(key)));
    return specificKeys.length ? specificKeys : permissionKeys;
  }, [permissionKeys]);
  const canView = hasModulePermission(permissionKeys, 'View', accessRole);
  const canProcess = !readOnly && hasModulePermission(submittedPermissionKeys, 'Edit', accessRole, false);
  const canUpdate = !readOnly && hasModulePermission(submittedPermissionKeys, 'Update', accessRole, false);
  const canEdit = canProcess || canUpdate;
  const availableLocations = useMemo(
    () => normalizeUserLocations(currentUser?.selected_locations || currentUser?.locations),
    [currentUser?.locations, currentUser?.selected_locations]
  );
  const [form, setForm] = useState(() => createEditForm(submittedRow, currentUser));
  const [vehicles, setVehicles] = useState(() => createEditVehicles(submittedRow));
  const [activeVehicleIndex] = useState(0);
  const [activePassengerIndexes, setActivePassengerIndexes] = useState([0]);
  const [activePedestrianIndex, setActivePedestrianIndex] = useState(0);
  const [activeWitnessIndex, setActiveWitnessIndex] = useState(0);
  const [existingUploadedFiles, setExistingUploadedFiles] = useState(() => buildExistingUploadedFiles(submittedRow));
  const [errors, setErrors] = useState({});
  const [successHeading, setSuccessHeading] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [impactEditor, setImpactEditor] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const activeLabel = STEP_ITEMS[activeStep];
  const todayDateInputValue = useMemo(() => getTodayDateInputValue(), []);
  const todayDateTimeInputValue = useMemo(() => getTodayDateTimeInputValue(), []);
  const totalUploadedFileCount = existingUploadedFiles.length + form.uploadedFiles.length;

  const requiredFields = useMemo(() => (
    activeStep === 0
      ? ['reportingEmployeeName', 'branchLocationCode']
      : activeStep === 1
        ? ['rentalAgreementNumber', 'authorizedDriverName']
      : activeStep === 2
          ? ['accidentDateTime']
          : activeStep === 3
            ? ['vehicleIdentification']
            : activeStep === 6
              ? ['uploadedFiles']
              : []
  ), [activeStep]);

  const handleChange = ({ target: { name, value, type, checked } }) => {
    setForm((prev) => {
      const nextValue = type === 'checkbox' ? checked : value;
      const nextForm = {
        ...prev,
        [name]: nextValue,
        ...(name === 'branchLocationCode' ? { claimOwningBranch: value } : {}),
        ...(name === 'relationshipToClaim' && value !== 'Other' ? { relationshipOther: '' } : {}),
        ...(name === 'claimPriorityLevel' && value !== 'Other' ? { claimPriorityLevelOther: '' } : {}),
        ...(name === 'claimSource' && value !== 'Other' ? { claimSourceOther: '' } : {}),
        ...(name === 'driverAtTimeOfAccident' && value !== 'Other' ? { driverAtTimeOfAccidentOther: '' } : {}),
        ...(name === 'insuranceType' && value !== 'Other' ? { insuranceTypeOther: '' } : {}),
        ...(name === 'ldwDeductibleAmount' && value !== 'Other' ? { ldwDeductibleAmountOther: '' } : {}),
        ...(name === 'ldwStatus' && value !== 'Accepted' ? { ldwDeductibleAmount: '', ldwDeductibleAmountOther: '' } : {}),
        ...(name === 'weatherConditions' && value !== 'Other' ? { weatherConditionsOther: '' } : {}),
        ...(name === 'roadConditions' && value !== 'Other' ? { roadConditionsOther: '' } : {}),
        ...(name === 'sameAsCellPhone' && checked ? { primaryContactPhone: prev.primaryContactCellPhone } : {}),
        ...(name === 'primaryContactCellPhone' && prev.sameAsCellPhone ? { primaryContactPhone: value } : {}),
      };

      if (name === 'accidentDateTime') {
        const [datePart = '', timePart = ''] = String(nextValue || '').split('T');
        nextForm.accidentDate = datePart;
        nextForm.accidentTime = timePart;
      }

      if (name === 'gpsLatitude' || name === 'gpsLongitude') {
        const latitude = name === 'gpsLatitude' ? nextValue : prev.gpsLatitude;
        const longitude = name === 'gpsLongitude' ? nextValue : prev.gpsLongitude;
        nextForm.gpsCoordinates = [latitude, longitude].filter(Boolean).join(', ');
      }

      if (name === 'country') {
        nextForm.state = getValidStateForCountry(prev.state, nextValue);
      }

      if (name === 'accidentCountry') {
        nextForm.accidentState = getValidStateForCountry(prev.accidentState, nextValue);
      }

      return nextForm;
    });

    setErrors((prev) => {
      if (
        !prev[name]
        && !(name === 'relationshipToClaim' && prev.relationshipOther)
        && !(name === 'claimPriorityLevel' && prev.claimPriorityLevelOther)
        && !(name === 'claimSource' && prev.claimSourceOther)
        && !(name === 'driverAtTimeOfAccident' && prev.driverAtTimeOfAccidentOther)
        && !(name === 'insuranceType' && prev.insuranceTypeOther)
        && !(name === 'ldwDeductibleAmount' && prev.ldwDeductibleAmountOther)
        && !(name === 'weatherConditions' && prev.weatherConditionsOther)
        && !(name === 'roadConditions' && prev.roadConditionsOther)
      ) {
        return prev;
      }

      const next = { ...prev };
      delete next[name];
      if (name === 'relationshipToClaim' && value !== 'Other') {
        delete next.relationshipOther;
      }
      if (name === 'claimPriorityLevel' && value !== 'Other') {
        delete next.claimPriorityLevelOther;
      }
      if (name === 'claimSource' && value !== 'Other') {
        delete next.claimSourceOther;
      }
      if (name === 'driverAtTimeOfAccident' && value !== 'Other') {
        delete next.driverAtTimeOfAccidentOther;
      }
      if (name === 'insuranceType' && value !== 'Other') {
        delete next.insuranceTypeOther;
      }
      if (name === 'ldwDeductibleAmount' && value !== 'Other') {
        delete next.ldwDeductibleAmountOther;
      }
      if (name === 'ldwStatus' && value !== 'Accepted') {
        delete next.ldwDeductibleAmount;
        delete next.ldwDeductibleAmountOther;
      }
      if (name === 'weatherConditions' && value !== 'Other') {
        delete next.weatherConditionsOther;
      }
      if (name === 'roadConditions' && value !== 'Other') {
        delete next.roadConditionsOther;
      }
      return next;
    });
  };

  const handleAccidentMapCoordinatesChange = useCallback(({ latitude, longitude }) => {
    setForm((prev) => ({
      ...prev,
      gpsLatitude: latitude,
      gpsLongitude: longitude,
      gpsCoordinates: [latitude, longitude].filter(Boolean).join(', '),
    }));
  }, []);

  const handleVehicleChange = ({ target: { name, value, type, checked } }) => {
    setVehicles((prev) => prev.map((vehicle, index) => (
      index === activeVehicleIndex
        ? (() => {
            const nextValue = type === 'checkbox' ? checked : value;
            const nextVehicle = { ...vehicle, [name]: nextValue };

            if (name === 'driverCountry') {
              nextVehicle.driverState = getValidStateForCountry(vehicle.driverState, nextValue);
            }

            if (name === 'ownerCountry') {
              nextVehicle.ownerState = getValidStateForCountry(vehicle.ownerState, nextValue);
            }

            if (name === 'vehicleStatus' && nextValue !== 'Other') {
              nextVehicle.vehicleStatusOther = '';
            }

            if (name === 'typeOfLoss' && nextValue !== 'Other') {
              nextVehicle.typeOfLossOther = '';
            }

            return nextVehicle;
          })()
        : vehicle
    )));

    setErrors((prev) => {
      if (!prev[name] && !(name === 'vehicleStatus' && prev.vehicleStatusOther) && !(name === 'typeOfLoss' && prev.typeOfLossOther)) return prev;
      const next = { ...prev };
      delete next[name];
      if (name === 'vehicleStatus' && value !== 'Other') {
        delete next.vehicleStatusOther;
      }
      if (name === 'typeOfLoss' && value !== 'Other') {
        delete next.typeOfLossOther;
      }
      return next;
    });
  };

  const openPointOfImpactEditor = (point) => {
    const savedSelections = Array.isArray(activeVehicle?.pointOfImpactSelections)
      ? activeVehicle.pointOfImpactSelections
      : [];
    const savedSelection = savedSelections.find((selection) => selection.pointId === point.id);

    setImpactEditor({
      pointId: point.id,
      label: point.label,
      details: savedSelection?.details || '',
    });
  };

  const closePointOfImpactEditor = () => {
    setImpactEditor(null);
  };

  const handleImpactEditorChange = ({ target: { name, value } }) => {
    setImpactEditor((prev) => (prev ? { ...prev, [name]: value } : prev));
  };

  const removePointOfImpactSelection = (pointId) => {
    setVehicles((prev) => prev.map((vehicle, index) => {
      if (index !== activeVehicleIndex) return vehicle;

      const currentSelections = Array.isArray(vehicle.pointOfImpactSelections)
        ? vehicle.pointOfImpactSelections
        : [];
      const nextSelections = currentSelections.filter((selection) => selection.pointId !== pointId);
      const primarySelection = nextSelections[0];

      return {
        ...vehicle,
        pointOfImpactSelections: nextSelections,
        pointOfImpactPosition: primarySelection?.pointId || '',
        pointOfImpactCode: '',
        pointOfImpactDetails: primarySelection?.details || '',
        pointOfImpact: nextSelections
          .map((selection) => [selection.label, selection.details].filter(Boolean).join(' - '))
          .join('; '),
      };
    }));
  };

  const savePointOfImpact = () => {
    if (!impactEditor) return;

    const nextDetails = String(impactEditor.details || '').trim();
    const hasImpactValue = Boolean(nextDetails);

    setVehicles((prev) => prev.map((vehicle, index) => {
      if (index !== activeVehicleIndex) return vehicle;

      const currentSelections = Array.isArray(vehicle.pointOfImpactSelections)
        ? vehicle.pointOfImpactSelections
        : [];
      const remainingSelections = currentSelections.filter((selection) => selection.pointId !== impactEditor.pointId);
      const nextSelections = hasImpactValue
        ? [
            ...remainingSelections,
            {
              pointId: impactEditor.pointId,
              label: impactEditor.label,
              details: nextDetails,
            },
          ]
        : remainingSelections;
      const sortedSelections = POINT_OF_IMPACT_POINTS
        .map((point) => nextSelections.find((selection) => selection.pointId === point.id))
        .filter(Boolean);
      const primarySelection = sortedSelections[0];

      return {
        ...vehicle,
        pointOfImpactSelections: sortedSelections,
        pointOfImpactPosition: primarySelection?.pointId || '',
        pointOfImpactCode: '',
        pointOfImpactDetails: primarySelection?.details || '',
        pointOfImpact: sortedSelections
          .map((selection) => [selection.label, selection.details].filter(Boolean).join(' - '))
          .join('; '),
      };
    }));

    closePointOfImpactEditor();
  };

  const addPassenger = () => {
    setVehicles((prev) => prev.map((vehicle, index) => {
      if (index !== activeVehicleIndex) return vehicle;
      if (vehicle.passengers.length >= 4) return vehicle;
      const nextPassengers = [...vehicle.passengers, createPassengerForm()];
      setActivePassengerIndexes((current) => current.map((value, currentIndex) => (
        currentIndex === activeVehicleIndex ? nextPassengers.length - 1 : value
      )));
      return { ...vehicle, passengers: nextPassengers };
    }));
  };

  const handlePassengerChange = (passengerIndex, { target: { name, value } }) => {
    setVehicles((prev) => prev.map((vehicle, index) => (
      index === activeVehicleIndex
        ? {
            ...vehicle,
            passengers: vehicle.passengers.map((passenger, idx) => (
              idx === passengerIndex
                ? {
                    ...passenger,
                    [name]: value,
                    ...(name === 'country'
                      ? { state: getValidStateForCountry(passenger.state, value) }
                      : {}),
                  }
                : passenger
            )),
          }
        : vehicle
    )));
  };

  useEffect(() => {
    if (!submittedRow?._id) {
      if (!initialRow) {
        navigate(submittedFormsPath);
      }
    }
  }, [initialRow, navigate, submittedFormsPath, submittedRow]);

  useEffect(() => {
    setActivePassengerIndexes(vehicles.map(() => 0));
  }, [vehicles.length]);

  useEffect(() => {
    if (availableLocations.length === 1) {
      const onlyLocation = availableLocations[0];

      setForm((prev) => {
        if (prev.branchLocationCode === onlyLocation && prev.claimOwningBranch === onlyLocation) {
          return prev;
        }

        return {
          ...prev,
          branchLocationCode: prev.branchLocationCode || onlyLocation,
          claimOwningBranch: prev.claimOwningBranch || onlyLocation,
        };
      });
    }
  }, [availableLocations]);

  useEffect(() => {
    if (!form.reportDateTime) {
      setForm((prev) => ({
        ...prev,
        reportDateTime: createReportTimestamp(),
      }));
    }
  }, [form.reportDateTime]);

  useEffect(() => {
    if (form.primaryInsuredContact === 'Yes') {
      setForm((prev) => ({
        ...prev,
        primaryContactName: prev.reportingEmployeeName,
        primaryContactEmail: prev.email,
        primaryContactPhone: prev.cellPhone
      }));
    }else{
      setForm((prev) => ({
        ...prev,
        primaryContactName: '',
        primaryContactEmail: '',
        primaryContactPhone: ''
      }));
    }
  }, [
    form.primaryInsuredContact,
    form.reportingEmployeeName,
    form.email,
    form.cellPhone
  ]);

  useEffect(() => {
    if (!showSuccessModal) return;

    Swal.fire({
      icon: successHeading === 'Submission Completed' ? 'success' : 'error',
      title: successHeading,
      text: successMessage,
      confirmButtonColor: '#2f6fd0',
    }).finally(() => {
      setShowSuccessModal(false);
    });
  }, [showSuccessModal, successHeading, successMessage]);

  const addPedestrian = () => {
    if (form.pedestrians.length >= 2) return;
    setForm((prev) => ({
      ...prev,
      pedestrians: [...prev.pedestrians, createPedestrianForm()],
    }));
    setActivePedestrianIndex(form.pedestrians.length);
  };

  const handlePedestrianChange = (pedestrianIndex, { target: { name, value } }) => {
    setForm((prev) => ({
      ...prev,
      pedestrians: prev.pedestrians.map((pedestrian, idx) => (
        idx === pedestrianIndex
          ? {
              ...pedestrian,
              [name]: value,
              ...(name === 'country'
                ? { state: getValidStateForCountry(pedestrian.state, value) }
                : {}),
            }
          : pedestrian
      )),
    }));
  };

  const addWitness = () => {
    if (form.witnesses.length >= 2) return;
    setForm((prev) => ({
      ...prev,
      witnesses: [...prev.witnesses, createWitnessForm()],
    }));
    setActiveWitnessIndex(form.witnesses.length);
  };

  const handleWitnessChange = (witnessIndex, { target: { name, value } }) => {
    setForm((prev) => ({
      ...prev,
      witnesses: prev.witnesses.map((witness, idx) => (
        idx === witnessIndex ? { ...witness, [name]: value } : witness
      )),
    }));
  };

  const handleDocumentUpload = ({ target: { files } }) => {
    const uploadedFiles = Array.from(files ?? []);
    if (uploadedFiles.length === 0) return;

    if (uploadedFiles.some((file) => file.size > MAX_UPLOAD_SIZE_BYTES)) {
      setErrors((prev) => ({ ...prev, uploadedFiles: 'Each file must be 1 GB or less.' }));
      return;
    }

    const currentTotalSize = form.uploadedFiles.reduce((total, file) => total + file.size, 0);
    const addedTotalSize = uploadedFiles.reduce((total, file) => total + file.size, 0);
    if (currentTotalSize + addedTotalSize > MAX_UPLOAD_SIZE_BYTES) {
      setErrors((prev) => ({ ...prev, uploadedFiles: 'The total upload size must be 1 GB or less.' }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      uploadedFiles: [...prev.uploadedFiles, ...uploadedFiles],
    }));
    setErrors((prev) => {
      if (!prev.uploadedFiles) return prev;
      const next = { ...prev };
      delete next.uploadedFiles;
      return next;
    });
  };

  const toggleDocumentDefinitions = () => {
    setForm((prev) => ({
      ...prev,
      showDocumentDefinitions: !prev.showDocumentDefinitions,
    }));
  };

  const removeUploadedFile = (fileIndexToDelete) => {
    setForm((prev) => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles.filter((_, fileIndex) => fileIndex !== fileIndexToDelete),
    }));
  };

  const removeExistingUploadedFile = (fileIdToDelete) => {
    setExistingUploadedFiles((prev) => prev.filter((file) => file.id !== fileIdToDelete));
  };

  const handleDownloadFile = (filePath) => {
    const downloadUrl = buildFileUrl(baseURL, filePath, true);
    if (!downloadUrl) return;

    window.open(downloadUrl, '_blank', 'noopener,noreferrer');
  };

  const handleViewExistingFile = (filePath) => {
    const previewUrl = buildFileUrl(baseURL, filePath);
    if (!previewUrl) return;

    window.open(previewUrl, '_blank', 'noopener,noreferrer');
  };

  const handleViewAllExistingFiles = () => openDocumentViewer(existingUploadedFiles.map((file) => ({
    name: file.original_name,
    url: buildFileUrl(baseURL, file.path),
    contentType: file.content_type,
  })));

  const handleViewNewFile = (file) => {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    window.open(previewUrl, '_blank', 'noopener,noreferrer');
    window.setTimeout(() => URL.revokeObjectURL(previewUrl), 60000);
  };

  const validateCurrentStep = () => {
    const nextErrors = {};

    requiredFields.forEach((field) => {
      if (field === 'uploadedFiles') {
        if (totalUploadedFileCount === 0) nextErrors.uploadedFiles = true;
        return;
      }

      if (field === 'vehicleIdentification') {
        if (!String(activeVehicle?.unitFleetNumber || '').trim() && !String(activeVehicle?.vin || '').trim()) {
          nextErrors.unitFleetNumber = true;
          nextErrors.vin = true;
        }
        return;
      }

      if (!String(form[field] ?? '').trim()) nextErrors[field] = true;
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateCurrentStep()) return;
    setActiveStep((prev) => Math.min(prev + 1, STEP_ITEMS.length - 1));
    window.scrollTo({
      top:0,
      behavior:'smooth'
    });
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({
      top:0,
      behavior:'smooth'
    });
  };
  const handleSubmit = async (moveToProcessing = promoteToProcessingOnSubmit) => {
    if (!canEdit || !submittedRow?._id) {
      Swal.fire({
        icon: 'warning',
        text: 'You do not have permission to update submitted forms.',
      });
      return;
    }

    if (!validateCurrentStep()) return;

    if (moveToProcessing) {
      const confirmation = await Swal.fire({
        icon: 'question',
        title: 'Move to Claims Processing?',
        text: 'Please confirm that you want to submit this claim to Claims Processing.',
        showCancelButton: true,
        confirmButtonText: 'Yes, continue',
        cancelButtonText: 'Cancel',
      });
      if (!confirmation.isConfirmed) return;
    }

    try {
      setSubmitting(true);

      const updatedForm = {
        ...form,
        updated_by: currentUser?.userName || 'System',
      };
      const formData = new FormData();
      // append form fields
      Object.keys(updatedForm).forEach((key) => {
        if (key !== 'uploadedFiles' && key !== 'witnesses' && key !== 'pedestrians') {
          formData.append(key, updatedForm[key]);
        }
      });

      // append vehicles
      formData.append(
        'details_by_vehicle',
        JSON.stringify(vehicles)
      );
      // append witnesses
      formData.append(
        'witnesses',
        JSON.stringify(form.witnesses || [])
      );

      // append pedestrians
      formData.append(
        'pedestrians',
        JSON.stringify(form.pedestrians || [])
      );
      formData.append('retainedUploadedFiles', JSON.stringify(existingUploadedFiles));
      // append files
      form.uploadedFiles.forEach((file) => {
        formData.append('uploadedFiles', file);
      });
      if (workflowTag) {
        formData.append('claim_workflow', workflowTag);
      }
      const respons = await axios.post(
        `${baseURL}${updateEndpointBase}/${submittedRow._id}`,
        formData
      );
      if (moveToProcessing) {
        await axios.post(`${baseURL}${promoteEndpointBase}/${submittedRow._id}`, {
          updated_by: currentUser?.userName || 'System',
        });
      }
      setShowSuccessModal(true);
      setSuccessHeading("Update Completed");
      setSuccessMessage(moveToProcessing
        ? 'The claim has been moved to Claims Processing.'
        : (respons?.data?.message || `The ${workflowName} has been updated successfully.`));
      setSubmitting(false);
      if (onSaved) {
        onSaved();
      }
      if (onCancel) {
        onCancel();
      } else {
        navigate(submittedFormsPath);
      }
    }catch(error){
      setSubmitting(false);
      setShowSuccessModal(true);
      setSuccessHeading("Failed");
      setSuccessMessage(error?.response?.data?.message || "Please try again!");
    }
  };

  const renderRadioGroup = (name, value, options) => (
    <div className="open-form-radio-group">
      {options.map((option) => (
        <label key={option} className="open-form-radio">
          <input
            type="radio"
            name={name}
            value={option}
            checked={value === option}
            onChange={handleChange}
          />
          <span>{option}</span>
        </label>
      ))}
    </div>
  );

  const renderStartStep = () => (
    <div className="open-form-card">
      <div className="open-form-grid">
        <div className="open-form-field">
          <label htmlFor="open-form-reporting-employee-name">
            <span className="required mandatory-required">*</span>
            {' '}
            Reporting Employee Name
          </label>
          <input
            id="open-form-reporting-employee-name"
            name="reportingEmployeeName"
            value={form.reportingEmployeeName}
            onChange={handleChange}
            className={errors.reportingEmployeeName ? 'has-error' : ''}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-reporting-employee-id">
            <span className="required">*</span>
            {' '}
            Reporting Employee ID
          </label>
          <input
            id="open-form-reporting-employee-id"
            name="reportingEmployeeId"
            value={form.reportingEmployeeId}
            readOnly
            className={errors.reportingEmployeeId ? 'has-error' : ''}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-branch-location-code">
            <span className="required mandatory-required">*</span>
            {' '}
            Branch / Location Code
          </label>
          <select
            id="open-form-branch-location-code"
            name="branchLocationCode"
            value={form.branchLocationCode}
            onChange={handleChange}
            className={errors.branchLocationCode ? 'has-error' : ''}
          >
            <option value="">Select location</option>
            {availableLocations.map((location) => (
              <option key={location} value={location}>
                {location}
              </option>
            ))}
          </select>
        </div>

        <OpenFormDateTimeField
          id="open-form-report-datetime"
          name="reportDateTime"
          label="Date & Time of Report"
          value={form.reportDateTime || createReportTimestamp()}
          onChange={handleChange}
          timezoneId="open-form-report-datetime-timezone"
          timezoneName="reportDateTimeTimezone"
          timezoneValue={form.reportDateTimeTimezone}
        />

        <OpenFormDateTimeField
          id="open-form-customer-reported-datetime"
          name="customerReportedDateTime"
          label="Renter Reported Date & Time"
          value={form.customerReportedDateTime}
          onChange={handleChange}
          timezoneId="open-form-customer-reported-datetime-timezone"
          timezoneName="customerReportedDateTimeTimezone"
          timezoneValue={form.customerReportedDateTimeTimezone}
        />

        <div className="open-form-field">
          <label htmlFor="open-form-claim-priority-level">
            {!isWindshieldTire && <><span className="required">*</span>{' '}</>}
            Claim Priority Level
          </label>
          <select
            id="open-form-claim-priority-level"
            name="claimPriorityLevel"
            value={form.claimPriorityLevel}
            onChange={handleChange}
            className={errors.claimPriorityLevel ? 'has-error' : ''}
          >
            {CLAIM_PRIORITY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {form.claimPriorityLevel === 'Other' && (
          <div className="open-form-field">
            <label htmlFor="open-form-claim-priority-level-other">
              <span className="required">*</span>
              {' '}
              Claim Priority Level - Other
            </label>
            <input
              id="open-form-claim-priority-level-other"
              name="claimPriorityLevelOther"
              value={form.claimPriorityLevelOther}
              onChange={handleChange}
              className={errors.claimPriorityLevelOther ? 'has-error' : ''}
              placeholder="Enter claim priority level"
            />
          </div>
        )}

        <div className="open-form-field">
          <label htmlFor="open-form-claim-source">
            <span className="required">*</span>
            {' '}
            Claim Source
          </label>
          <select
            id="open-form-claim-source"
            name="claimSource"
            value={form.claimSource}
            onChange={handleChange}
            className={errors.claimSource ? 'has-error' : ''}
          >
            {CLAIM_SOURCE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {form.claimSource === 'Other' && (
          <div className="open-form-field">
            <label htmlFor="open-form-claim-source-other">
              <span className="required">*</span>
              {' '}
              Claim Source - Other
            </label>
            <input
              id="open-form-claim-source-other"
              name="claimSourceOther"
              value={form.claimSourceOther}
              onChange={handleChange}
              className={errors.claimSourceOther ? 'has-error' : ''}
              placeholder="Enter claim source"
            />
          </div>
        )}
      </div>
    </div>
  );

  const renderRoutesInsuredStep = () => (
    <div className="open-form-card">
      <div className="open-form-grid">
        <div className="open-form-field">
          <label htmlFor="open-form-rental-agreement-number">
            <span className="required mandatory-required">*</span>
            {' '}
            Rental Agreement (RA) Number
          </label>
          <input
            id="open-form-rental-agreement-number"
            name="rentalAgreementNumber"
            value={form.rentalAgreementNumber}
            onChange={handleChange}
            className={errors.rentalAgreementNumber ? 'has-error' : ''}
          />
        </div>

        {isWindshieldTire && (
          <div className="open-form-field">
            <label htmlFor="open-form-tire-glass-waiver">
              <span className="required">*</span>{' '}Tire &amp; Glass Waiver
            </label>
            <select id="open-form-tire-glass-waiver" name="tireGlassWaiver" value={form.tireGlassWaiver} onChange={handleChange} className={errors.tireGlassWaiver ? 'has-error' : ''}>
              <option value="" />
              <option value="Yes">Yes</option>
              <option value="No">No</option>
            </select>
          </div>
        )}

        <div className="open-form-field">
          <label htmlFor="open-form-authorized-driver-name">
            <span className="required mandatory-required">*</span>
            {' '}
            Renter Driver Name
          </label>
          <input
            id="open-form-authorized-driver-name"
            name="authorizedDriverName"
            value={form.authorizedDriverName}
            onChange={handleChange}
            className={errors.authorizedDriverName ? 'has-error' : ''}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-driver-at-accident">
            <span className="required">*</span>
            {' '}
            Who Was Driving
          </label>
          <select
            id="open-form-driver-at-accident"
            name="driverAtTimeOfAccident"
            value={form.driverAtTimeOfAccident}
            onChange={handleChange}
            className={errors.driverAtTimeOfAccident ? 'has-error' : ''}
          >
            {DRIVER_SELECTION_OPTIONS.map((option) => (
              <option key={option || 'blank'} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {form.driverAtTimeOfAccident === 'Other' && (
          <div className="open-form-field">
            <label htmlFor="open-form-driver-at-accident-other">
              <span className="required">*</span>
              {' '}
              Driver Name
            </label>
            <input
              id="open-form-driver-at-accident-other"
              name="driverAtTimeOfAccidentOther"
              value={form.driverAtTimeOfAccidentOther}
              onChange={handleChange}
              className={errors.driverAtTimeOfAccidentOther ? 'has-error' : ''}
              placeholder="Enter driver name"
            />
          </div>
        )}

        <div className="open-form-field">
          <label htmlFor="open-form-pre-authorization-amount">
            <span className="required">*</span>
            {' '}
            Credit card pre-authorization/deposit taken
          </label>
          <input
            id="open-form-pre-authorization-amount"
            name="preAuthorizationAmount"
            value={form.preAuthorizationAmount}
            onChange={handleChange}
            className={errors.preAuthorizationAmount ? 'has-error' : ''}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-customer-phone">
            <span className="required">*</span>
            {' '}
            Renter Phone Number
          </label>
          <input
            id="open-form-customer-phone"
            name="customerPhone"
            value={form.customerPhone}
            onChange={handleChange}
            className={errors.customerPhone ? 'has-error' : ''}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-customer-email">
            <span className="required">*</span>
            {' '}
            Renter Email Address
          </label>
          <input
            id="open-form-customer-email"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            className={errors.email ? 'has-error' : ''}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-ldw-status">Excess Damage Waiver (LDW/CDW) Status</label>
          <select
            id="open-form-ldw-status"
            name="ldwStatus"
            value={form.ldwStatus}
            onChange={handleChange}
          >
            {LDW_STATUS_OPTIONS.map((option) => (
              <option key={option || 'blank'} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {form.ldwStatus === 'Accepted' && (
          <>
            <div className="open-form-field">
              <label htmlFor="open-form-ldw-deductible-amount">
                <span className="required">*</span>
                {' '}
                Deductible Amount
              </label>
              <select
                id="open-form-ldw-deductible-amount"
                name="ldwDeductibleAmount"
                value={form.ldwDeductibleAmount}
                onChange={handleChange}
                className={errors.ldwDeductibleAmount ? 'has-error' : ''}
              >
                {LDW_DEDUCTIBLE_AMOUNT_OPTIONS.map((option) => (
                  <option key={option || 'blank'} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>

            {form.ldwDeductibleAmount === 'Other' && (
              <div className="open-form-field">
                <label htmlFor="open-form-ldw-deductible-amount-other">
                  <span className="required">*</span>
                  {' '}
                  Deductible Amount - Other
                </label>
                <input
                  id="open-form-ldw-deductible-amount-other"
                  name="ldwDeductibleAmountOther"
                  value={form.ldwDeductibleAmountOther}
                  onChange={handleChange}
                  className={errors.ldwDeductibleAmountOther ? 'has-error' : ''}
                  placeholder="Enter deductible amount"
                />
              </div>
            )}
          </>
        )}

        <div className="open-form-field">
          <label htmlFor="open-form-customer-insurance-carrier">
            <span className="required">*</span>
            {' '}
            Insurance Company Name
          </label>
          <input
            id="open-form-customer-insurance-carrier"
            name="customerInsuranceCarrier"
            value={form.customerInsuranceCarrier}
            onChange={handleChange}
            className={errors.customerInsuranceCarrier ? 'has-error' : ''}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-insurance-type">
            <span className="required">*</span>
            {' '}
            Insurance Type
          </label>
          <select
            id="open-form-insurance-type"
            name="insuranceType"
            value={form.insuranceType}
            onChange={handleChange}
            className={errors.insuranceType ? 'has-error' : ''}
          >
            {INSURANCE_TYPE_OPTIONS.map((option) => (
              <option key={option || 'blank'} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {form.insuranceType === 'Other' && (
          <div className="open-form-field">
            <label htmlFor="open-form-insurance-type-other">
              <span className="required">*</span>
              {' '}
              Insurance Type - Other
            </label>
            <input
              id="open-form-insurance-type-other"
              name="insuranceTypeOther"
              value={form.insuranceTypeOther}
              onChange={handleChange}
              className={errors.insuranceTypeOther ? 'has-error' : ''}
              placeholder="Enter insurance type"
            />
          </div>
        )}

        <div className="open-form-field">
          <label htmlFor="open-form-customer-policy-number">
            <span className="required">*</span>
            {' '}
            Policy Number
          </label>
          <input
            id="open-form-customer-policy-number"
            name="customerPolicyNumber"
            value={form.customerPolicyNumber}
            onChange={handleChange}
            className={errors.customerPolicyNumber ? 'has-error' : ''}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-claim-owning-branch">
            <span className="required">*</span>
            {' '}
            Branch / Location Owning the Claim
          </label>
          <input
            id="open-form-claim-owning-branch"
            name="claimOwningBranch"
            value={form.claimOwningBranch}
            onChange={handleChange}
            className={errors.claimOwningBranch ? 'has-error' : ''}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-rental-start-date">
            <span className="required">*</span>
            {' '}
            Rental Start Date
          </label>
          <OpenFormDatePicker
            id="open-form-rental-start-date"
            name="rentalStartDate"
            value={form.rentalStartDate}
            onChange={handleChange}
            max={form.rentalReturnDate || todayDateInputValue}
            error={errors.rentalStartDate}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-rental-return-date">
            <span className="required">*</span>
            {' '}
            Rental Return Date
          </label>
          <OpenFormDatePicker
            id="open-form-rental-return-date"
            name="rentalReturnDate"
            value={form.rentalReturnDate}
            onChange={handleChange}
            min={form.rentalStartDate || ''}
            max={todayDateInputValue}
            error={errors.rentalReturnDate}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-original-rental-return-date">Original Rental Return Date</label>
          <OpenFormDatePicker
            id="open-form-original-rental-return-date"
            name="originalRentalReturnDate"
            value={form.originalRentalReturnDate}
            onChange={handleChange}
            min={form.rentalReturnDate || ''}
            error={errors.originalRentalReturnDate}
          />
        </div>
      </div>
    </div>
  );

  const renderAuthorityRow = (checkboxName, label, nameField, reportField) => (
    <div className="open-form-authority-row" key={checkboxName}>
      <label className={`open-form-checkbox ${errors[checkboxName] ? 'has-error' : ''}`.trim()}>
        <input
          type="checkbox"
          name={checkboxName}
          checked={form[checkboxName]}
          onChange={() => {}}
          disabled
        />
        <span>
          {label}
          {' '}
          <span className="required">*</span>
        </span>
      </label>

      <div className="open-form-field">
        <input
          name={nameField}
          value={form[nameField]}
          onChange={handleChange}
          className={errors[nameField] ? 'has-error' : ''}
        />
      </div>

      <div className="open-form-field">
        <input
          name={reportField}
          value={form[reportField]}
          onChange={handleChange}
          className={errors[reportField] ? 'has-error' : ''}
        />
      </div>
    </div>
  );

  const renderAccidentOverviewStep = () => (
    <div className="open-form-card">
      <div className="open-form-grid">
        <OpenFormDateTimeField
          id="open-form-accident-datetime"
          name="accidentDateTime"
          label="Accident Date & Time"
          value={form.accidentDateTime}
          onChange={handleChange}
          timezoneId="open-form-accident-datetime-timezone"
          timezoneName="accidentDateTimeTimezone"
          timezoneValue={form.accidentDateTimeTimezone}
          required
          max={todayDateTimeInputValue}
          error={errors.accidentDateTime}
        />

        <div className="open-form-field">
          <label htmlFor="open-form-reported-to-authorities">Is It Reported To Authorities (Police or Other)</label>
          <select
            id="open-form-reported-to-authorities"
            name="reportedToAuthorities"
            value={form.reportedToAuthorities}
            onChange={handleChange}
          >
            {YES_NO_OPTIONS.map((option) => (
              <option key={option || 'blank'} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="open-form-grid">
        <div className="open-form-field" style={{ gridColumn: '1 / -1' }}>
          <label htmlFor="open-form-accident-location">
            <span className="required">*</span>
            {' '}
            Accident Location
          </label>
          <input
            id="open-form-accident-location"
            name="accidentLocation"
            value={form.accidentLocation}
            onChange={handleChange}
            className={errors.accidentLocation ? 'has-error' : ''}
          />
        </div>
      </div>

      <div className="open-form-location-map-grid">
        <div className="open-form-location-coordinates">
          <div className="open-form-field">
            <label htmlFor="open-form-gps-latitude">GPS Latitude</label>
            <input
              id="open-form-gps-latitude"
              name="gpsLatitude"
              value={form.gpsLatitude}
              onChange={handleChange}
              placeholder="Enter latitude"
            />
          </div>
          <div className="open-form-field">
            <label htmlFor="open-form-gps-longitude">GPS Longitude</label>
            <input
              id="open-form-gps-longitude"
              name="gpsLongitude"
              value={form.gpsLongitude}
              onChange={handleChange}
              placeholder="Enter longitude"
            />
          </div>
        </div>

        <AccidentLocationMap
          address={form.accidentLocation}
          latitude={form.gpsLatitude}
          longitude={form.gpsLongitude}
          onCoordinatesChange={handleAccidentMapCoordinatesChange}
        />
      </div>

      <div className="open-form-grid open-form-grid-four">
        <div className="open-form-field">
          <label htmlFor="open-form-accident-address">Street Address</label>
          <input
            id="open-form-accident-address"
            name="accidentAddress"
            value={form.accidentAddress}
            onChange={handleChange}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-accident-city">City</label>
          <input
            id="open-form-accident-city"
            name="accidentCity"
            value={form.accidentCity}
            onChange={handleChange}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-accident-state">Province / State</label>
          <select
            id="open-form-accident-state"
            name="accidentState"
            value={form.accidentState}
            onChange={handleChange}
          >
            {getStateOptionsByCountry(form.accidentCountry).map((option) => (
              <option key={option || 'blank'} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-accident-country">Country</label>
          <select
            id="open-form-accident-country"
            name="accidentCountry"
            value={form.accidentCountry}
            onChange={handleChange}
          >
            {COUNTRY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="open-form-field open-form-field-textarea">
        <label htmlFor="open-form-accident-description">
          <span className="required">*</span>
          {' '}
          Detailed Accident Description
        </label>
        <textarea
          id="open-form-accident-description"
          name="accidentDescription"
          value={form.accidentDescription}
          onChange={handleChange}
          className={errors.accidentDescription ? 'has-error' : ''}
          maxLength={2500}
        />
        <div className="open-form-character-count">
          {2500 - form.accidentDescription.length}
          {' '}
          characters remaining
        </div>
      </div>

      <div className="open-form-grid open-form-grid-four">
        <div className="open-form-field">
          <label htmlFor="open-form-weather-conditions">
            {!isWindshieldTire && <><span className="required">*</span>{' '}</>}
            Weather Conditions
          </label>
          <select
            id="open-form-weather-conditions"
            name="weatherConditions"
            value={form.weatherConditions}
            onChange={handleChange}
            className={errors.weatherConditions ? 'has-error' : ''}
          >
            {WEATHER_CONDITION_OPTIONS.map((option) => (
              <option key={option || 'blank'} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {form.weatherConditions === 'Other' && (
          <div className="open-form-field">
            <label htmlFor="open-form-weather-conditions-other">
              <span className="required">*</span>
              {' '}
              Weather Conditions - Other
            </label>
            <input
              id="open-form-weather-conditions-other"
              name="weatherConditionsOther"
              value={form.weatherConditionsOther}
              onChange={handleChange}
              className={errors.weatherConditionsOther ? 'has-error' : ''}
              placeholder="Enter weather condition"
            />
          </div>
        )}

        <div className="open-form-field">
          <label htmlFor="open-form-road-conditions">
            <span className="required">*</span>
            {' '}
            Road Conditions
          </label>
          <select
            id="open-form-road-conditions"
            name="roadConditions"
            value={form.roadConditions}
            onChange={handleChange}
            className={errors.roadConditions ? 'has-error' : ''}
          >
            {ROAD_CONDITION_OPTIONS.map((option) => (
              <option key={option || 'blank'} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {form.roadConditions === 'Other' && (
          <div className="open-form-field">
            <label htmlFor="open-form-road-conditions-other">
              <span className="required">*</span>
              {' '}
              Road Conditions - Other
            </label>
            <input
              id="open-form-road-conditions-other"
              name="roadConditionsOther"
              value={form.roadConditionsOther}
              onChange={handleChange}
              className={errors.roadConditionsOther ? 'has-error' : ''}
              placeholder="Enter road condition"
            />
          </div>
        )}

        <div className="open-form-field">
          <label htmlFor="open-form-third-party-driver-name">Third Party Driver Name</label>
          <input
            id="open-form-third-party-driver-name"
            name="thirdPartyDriverName"
            value={form.thirdPartyDriverName}
            onChange={handleChange}
          />
        </div>

        <div className="open-form-field">
          <label htmlFor="open-form-third-party-plate-number">Third Party Vehicle Details</label>
          <input
            id="open-form-third-party-plate-number"
            name="thirdPartyPlateNumber"
            value={form.thirdPartyPlateNumber}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="open-form-field open-form-field-textarea">
        <label htmlFor="open-form-third-party-insurance-details">Third Party Insurance Details</label>
        <textarea
          id="open-form-third-party-insurance-details"
          name="thirdPartyInsuranceDetails"
          value={form.thirdPartyInsuranceDetails}
          onChange={handleChange}
          maxLength={1000}
        />
      </div>

      <div className="open-form-question">
        <div className="open-form-question-title">
          <span>Authorities Contacted</span>
        </div>
      </div>
      {Object.keys(errors).some((key) => (
        key === 'policeContacted'
        || key === 'fireDepartmentContacted'
        || key === 'ambulanceContacted'
        || key === 'otherAuthorityContacted'
        || key === 'policeName'
        || key === 'policeReportNumber'
        || key === 'fireDepartmentName'
        || key === 'fireDepartmentReportNumber'
        || key === 'ambulanceName'
        || key === 'ambulanceReportNumber'
        || key === 'otherAuthorityName'
        || key === 'otherAuthorityReportNumber'
      )) ? (
        <div className="open-form-inline-error">
          Check all four authorities and complete every Name and Report Number field.
        </div>
      ) : null}

      <div className="open-form-authority-header">
        <span />
        <span>Name</span>
        <span>Report Number</span>
      </div>

      <div className="open-form-authority-list">
        {renderAuthorityRow('policeContacted', 'Police/Sheriff/Highway Patrol/etc.', 'policeName', 'policeReportNumber')}
        {renderAuthorityRow('fireDepartmentContacted', 'Fire Department', 'fireDepartmentName', 'fireDepartmentReportNumber')}
        {renderAuthorityRow('ambulanceContacted', 'Ambulance', 'ambulanceName', 'ambulanceReportNumber')}
        {renderAuthorityRow('otherAuthorityContacted', 'Other', 'otherAuthorityName', 'otherAuthorityReportNumber')}
      </div>
    </div>
  );

  const activeVehicle = vehicles[activeVehicleIndex];

  const pointOfImpactSelections = Array.isArray(activeVehicle.pointOfImpactSelections)
    ? activeVehicle.pointOfImpactSelections
    : [];
  const hasSavedPointOfImpact = pointOfImpactSelections.length > 0;

  const renderPointOfImpactSelector = () => (
    <div className="open-form-impact-selector">
      <div className="open-form-impact-diagram">
        <img className="open-form-impact-car" src={pointOfImpactDiagram} alt="Point of impact vehicle diagram" />

        {POINT_OF_IMPACT_POINTS.map((point) => {
          const isChecked = pointOfImpactSelections.some((selection) => selection.pointId === point.id);

          return (
            <button
              key={point.id}
              type="button"
              className={`open-form-impact-point${isChecked ? ' selected' : ''}`}
              style={{ left: `${point.x}%`, top: `${point.y}%` }}
              onClick={() => {
                if (isChecked) {
                  removePointOfImpactSelection(point.id);
                } else {
                  openPointOfImpactEditor(point);
                }
              }}
              aria-label={`${isChecked ? 'Remove' : 'Add'} ${point.label}`}
              title={point.label}
              aria-pressed={isChecked}
            >
              <input type="checkbox" checked={isChecked} readOnly tabIndex="-1" aria-hidden="true" />
            </button>
          );
        })}
      </div>

      {hasSavedPointOfImpact && (
        <div className="open-form-impact-summary">
          {pointOfImpactSelections.map((selection) => (
            <span key={selection.pointId}>
              <strong>{selection.label}</strong>
              {selection.details ? ` - ${selection.details}` : ''}
            </span>
          ))}
        </div>
      )}
    </div>
  );

  const renderVehicleRadioGroup = (name, value, options, errorText = '') => (
    <div className="open-form-inline-radio-block">
      <div className="open-form-radio-group">
        {options.map((option) => (
          <label key={option} className="open-form-radio">
            <input
              type="radio"
              name={name}
              value={option}
              checked={value === option}
              onChange={handleVehicleChange}
            />
            <span>{option}</span>
          </label>
        ))}
      </div>
      {errorText ? <span className="open-form-inline-error">{errorText}</span> : null}
    </div>
  );

  const deletePassenger = (passengerIndexToDelete) => {
    setVehicles((prev) => prev.map((vehicle, index) => {
      if (index !== activeVehicleIndex || vehicle.passengers.length === 1) return vehicle;

      const nextPassengers = vehicle.passengers.filter((_, passengerIndex) => passengerIndex !== passengerIndexToDelete);
      setActivePassengerIndexes((current) => current.map((value, currentIndex) => {
        if (currentIndex !== activeVehicleIndex) return value;
        if (value > passengerIndexToDelete) return value - 1;
        if (value >= nextPassengers.length) return nextPassengers.length - 1;
        return value;
      }));

      return {
        ...vehicle,
        passengers: nextPassengers,
      };
    }));
  };

  const deletePedestrian = (pedestrianIndexToDelete) => {
    setForm((prev) => {
      if (prev.pedestrians.length === 1) return prev;
      const nextPedestrians = prev.pedestrians.filter((_, pedestrianIndex) => pedestrianIndex !== pedestrianIndexToDelete);
      setActivePedestrianIndex((current) => {
        if (current > pedestrianIndexToDelete) return current - 1;
        if (current >= nextPedestrians.length) return nextPedestrians.length - 1;
        return current;
      });
      return {
        ...prev,
        pedestrians: nextPedestrians,
      };
    });
  };

  const deleteWitness = (witnessIndexToDelete) => {
    setForm((prev) => {
      if (prev.witnesses.length === 1) return prev;
      const nextWitnesses = prev.witnesses.filter((_, witnessIndex) => witnessIndex !== witnessIndexToDelete);
      setActiveWitnessIndex((current) => {
        if (current > witnessIndexToDelete) return current - 1;
        if (current >= nextWitnesses.length) return nextWitnesses.length - 1;
        return current;
      });
      return {
        ...prev,
        witnesses: nextWitnesses,
      };
    });
  };

  const activePassengerIndex = activePassengerIndexes[activeVehicleIndex] ?? 0;
  const activePassenger = activeVehicle.passengers[activePassengerIndex];

  const renderPassengerCard = (passenger, passengerIndex) => (
    <div className="open-form-passenger-card" key={`passenger-${passengerIndex + 1}`}>
      <div className="open-form-grid">
        <div className="open-form-field">
          <label>First Name</label>
          <input name="firstName" value={passenger.firstName} onChange={(event) => handlePassengerChange(passengerIndex, event)} />
        </div>
        <div className="open-form-field">
          <label>
            <span className="required">*</span>
            {' '}
            Last Name
          </label>
          <input name="lastName" value={passenger.lastName} onChange={(event) => handlePassengerChange(passengerIndex, event)} />
        </div>
      </div>

      <div className="open-form-grid open-form-grid-vehicle-contact">
        <div className="open-form-field">
          <label>
            <span className="required">*</span>
            {' '}
            Phone
          </label>
          <input name="phone" value={passenger.phone} onChange={(event) => handlePassengerChange(passengerIndex, event)} />
        </div>
        <div className="open-form-field">
          <label>Extension</label>
          <input name="extension" value={passenger.extension} onChange={(event) => handlePassengerChange(passengerIndex, event)} />
        </div>
        <div className="open-form-field">
          <label>Email</label>
          <input name="email" value={passenger.email} onChange={(event) => handlePassengerChange(passengerIndex, event)} />
        </div>
      </div>

      <div className="open-form-grid">
        <div className="open-form-field open-form-field-half-row">
          <label>Address</label>
          <input name="address" value={passenger.address} onChange={(event) => handlePassengerChange(passengerIndex, event)} />
        </div>
      </div>

      <div className="open-form-grid open-form-grid-four">
        <div className="open-form-field">
          <label>Zip Code</label>
          <input name="zipCode" value={passenger.zipCode} onChange={(event) => handlePassengerChange(passengerIndex, event)} />
        </div>
        <div className="open-form-field">
          <label>City</label>
          <input name="city" value={passenger.city} onChange={(event) => handlePassengerChange(passengerIndex, event)} />
        </div>
        <div className="open-form-field">
          <label>State</label>
          <select name="state" value={passenger.state} onChange={(event) => handlePassengerChange(passengerIndex, event)}>
            {getStateOptionsByCountry(passenger.country).map((option) => (
              <option key={option || 'blank'} value={option}>{option}</option>
            ))}
          </select>
        </div>
        <div className="open-form-field">
          <label>Country</label>
          <select name="country" value={passenger.country} onChange={(event) => handlePassengerChange(passengerIndex, event)}>
            {COUNTRY_OPTIONS.map((option) => (
              <option key={option} value={option}>{option}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="open-form-field open-form-field-textarea">
        <label>
          <span className="required">*</span>
          {' '}
          Describe the detail of injuries
        </label>
        <textarea
          name="injuryDescription"
          value={passenger.injuryDescription}
          onChange={(event) => handlePassengerChange(passengerIndex, event)}
          maxLength={100}
        />
        <div className="open-form-character-count">{100 - passenger.injuryDescription.length} characters remaining</div>
      </div>
    </div>
  );

  const renderPedestrianCard = (pedestrian, pedestrianIndex) => (
    <div className="open-form-passenger-card" key={`pedestrian-${pedestrianIndex + 1}`}>
      <div className="open-form-grid">
        <div className="open-form-field">
          <label>First Name</label>
          <input name="firstName" value={pedestrian.firstName} onChange={(event) => handlePedestrianChange(pedestrianIndex, event)} />
        </div>
        <div className="open-form-field">
          <label><span className="required">*</span> Last Name</label>
          <input name="lastName" value={pedestrian.lastName} onChange={(event) => handlePedestrianChange(pedestrianIndex, event)} />
        </div>
      </div>

      <div className="open-form-grid open-form-grid-vehicle-contact">
        <div className="open-form-field">
          <label>Phone</label>
          <input name="phone" value={pedestrian.phone} onChange={(event) => handlePedestrianChange(pedestrianIndex, event)} />
        </div>
        <div className="open-form-field">
          <label>Extension</label>
          <input name="extension" value={pedestrian.extension} onChange={(event) => handlePedestrianChange(pedestrianIndex, event)} />
        </div>
        <div className="open-form-field">
          <label>Email</label>
          <input name="email" value={pedestrian.email} onChange={(event) => handlePedestrianChange(pedestrianIndex, event)} />
        </div>
      </div>

      <div className="open-form-grid">
        <div className="open-form-field open-form-field-half-row">
          <label>Address</label>
          <input name="address" value={pedestrian.address} onChange={(event) => handlePedestrianChange(pedestrianIndex, event)} />
        </div>
      </div>

      <div className="open-form-grid open-form-grid-four">
        <div className="open-form-field">
          <label>Zip Code</label>
          <input name="zipCode" value={pedestrian.zipCode} onChange={(event) => handlePedestrianChange(pedestrianIndex, event)} />
        </div>
        <div className="open-form-field">
          <label>City</label>
          <input name="city" value={pedestrian.city} onChange={(event) => handlePedestrianChange(pedestrianIndex, event)} />
        </div>
        <div className="open-form-field">
          <label>State</label>
          <select name="state" value={pedestrian.state} onChange={(event) => handlePedestrianChange(pedestrianIndex, event)}>
            {getStateOptionsByCountry(pedestrian.country).map((option) => <option key={option || 'blank'} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="open-form-field">
          <label>Country</label>
          <select name="country" value={pedestrian.country} onChange={(event) => handlePedestrianChange(pedestrianIndex, event)}>
            {COUNTRY_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
      </div>

      <div className="open-form-field open-form-field-textarea">
        <label><span className="required">*</span> Describe the detail of injuries</label>
        <textarea
          name="injuryDescription"
          value={pedestrian.injuryDescription}
          onChange={(event) => handlePedestrianChange(pedestrianIndex, event)}
          maxLength={100}
        />
        <div className="open-form-character-count">{100 - pedestrian.injuryDescription.length} characters remaining</div>
      </div>
    </div>
  );

  const renderWitnessCard = (witness, witnessIndex) => (
    <div className="open-form-passenger-card" key={`witness-${witnessIndex + 1}`}>
      <div className="open-form-grid">
        <div className="open-form-field">
          <label>First Name</label>
          <input name="firstName" value={witness.firstName} onChange={(event) => handleWitnessChange(witnessIndex, event)} />
        </div>
        <div className="open-form-field">
          <label><span className="required">*</span> Last Name</label>
          <input name="lastName" value={witness.lastName} onChange={(event) => handleWitnessChange(witnessIndex, event)} />
        </div>
      </div>

      <div className="open-form-grid open-form-grid-vehicle-contact">
        <div className="open-form-field">
          <label>Phone</label>
          <input name="phone" value={witness.phone} onChange={(event) => handleWitnessChange(witnessIndex, event)} />
        </div>
        <div className="open-form-field">
          <label>Email</label>
          <input name="email" value={witness.email} onChange={(event) => handleWitnessChange(witnessIndex, event)} />
        </div>
      </div>
    </div>
  );

  const renderIncidentSpecificsStep = () => (
    <div className="open-form-card">
      <div className="open-form-grid open-form-grid-vehicle-four">
        <div className="open-form-field">
          <label htmlFor="open-form-type-of-loss">
            <span className="required">*</span>
            {' '}
            Type of Loss
          </label>
          <select
            id="open-form-type-of-loss"
            name="typeOfLoss"
            value={activeVehicle.typeOfLoss}
            onChange={handleVehicleChange}
            className={errors.typeOfLoss ? 'has-error' : ''}
          >
            {(isWindshieldTire ? ['', 'Windshield / Glass Damage', 'Tire Damage', 'Other'] : TYPE_OF_LOSS_OPTIONS).map((option) => (
              <option key={option || 'blank'} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        {activeVehicle.typeOfLoss === 'Other' && (
          <div className="open-form-field">
            <label htmlFor="open-form-type-of-loss-other">
              <span className="required">*</span>
              {' '}
              Type of Loss - Other
            </label>
            <input
              id="open-form-type-of-loss-other"
              name="typeOfLossOther"
              value={activeVehicle.typeOfLossOther}
              onChange={handleVehicleChange}
              className={errors.typeOfLossOther ? 'has-error' : ''}
              placeholder="Enter type of loss"
            />
          </div>
        )}

        <div className="open-form-field">
          <label htmlFor="open-form-drivability-status">Drivability Status</label>
          <select id="open-form-drivability-status" name="drivabilityStatus" value={activeVehicle.drivabilityStatus} onChange={handleVehicleChange}>
            {VEHICLE_STATUS_OPTIONS.map((option) => (
              <option key={option || 'blank'} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>

        <div className="open-form-field open-form-impact-field" style={{ gridColumn: 'span 2' }}>
          <label htmlFor="open-form-point-of-impact">Point of Impact Diagram / Visual Selector</label>
          {renderPointOfImpactSelector()}
        </div>
      </div>

      <div className="open-form-grid">
        <div className="open-form-field open-form-field-textarea">
          <label htmlFor="open-form-tow-provider-info">Tow Provider Information</label>
          <textarea
            id="open-form-tow-provider-info"
            name="towProviderInfo"
            value={activeVehicle.towProviderInfo}
            onChange={handleVehicleChange}
            rows={4}
          />
        </div>

        <div className="open-form-field open-form-field-textarea">
          <label htmlFor="open-form-storage-yard-info">Storage Yard Information</label>
          <textarea
            id="open-form-storage-yard-info"
            name="storageYardInfo"
            value={activeVehicle.storageYardInfo}
            onChange={handleVehicleChange}
            rows={4}
          />
        </div>
      </div>

      <div className="open-form-field open-form-field-textarea">
        <label htmlFor="open-form-vehicle-damage-description">Point of Impact / Damage Notes</label>
        <textarea
          id="open-form-vehicle-damage-description"
          name="vehicleDamageDescription"
          value={activeVehicle.vehicleDamageDescription}
          onChange={handleVehicleChange}
          maxLength={1000}
        />
        <div className="open-form-character-count">{1000 - activeVehicle.vehicleDamageDescription.length} characters remaining</div>
      </div>
    </div>
  );

  const renderAdditionalInformationStep = () => (
    <div className="open-form-card">
      <div className="open-form-question open-form-question-compact">
        <div className="open-form-question-title"><span>Was the driver injured?</span></div>
        {renderVehicleRadioGroup('wasDriverInjured', activeVehicle.wasDriverInjured, ADDITIONAL_INFORMATION_RADIO_OPTIONS)}
      </div>

      {activeVehicle.wasDriverInjured === 'Yes' && (
        <div className="open-form-field open-form-field-textarea">
          <label>Driver Injury Details</label>
          <textarea
            name="driverInjuryDescription"
            value={activeVehicle.driverInjuryDescription}
            onChange={handleVehicleChange}
            maxLength={500}
          />
          <div className="open-form-character-count">{500 - activeVehicle.driverInjuryDescription.length} characters remaining</div>
        </div>
      )}

      <div className="open-form-question open-form-question-compact">
        <div className="open-form-question-title"><span>Was the other driver injured?</span></div>
        {renderVehicleRadioGroup('wasOtherDriverInjured', activeVehicle.wasOtherDriverInjured, ADDITIONAL_INFORMATION_RADIO_OPTIONS)}
      </div>

      {activeVehicle.wasOtherDriverInjured === 'Yes' && (
        <div className="open-form-field open-form-field-textarea">
          <label>Other Driver Injury Details</label>
          <textarea
            name="otherDriverInjuryDescription"
            value={activeVehicle.otherDriverInjuryDescription}
            onChange={handleVehicleChange}
            maxLength={500}
          />
          <div className="open-form-character-count">{500 - activeVehicle.otherDriverInjuryDescription.length} characters remaining</div>
        </div>
      )}

      <div className="open-form-question open-form-question-compact">
        <div className="open-form-question-title"><span>Were passengers injured?</span></div>
        {renderVehicleRadioGroup('werePassengersInjured', activeVehicle.werePassengersInjured, ADDITIONAL_INFORMATION_RADIO_OPTIONS)}
      </div>

      {activeVehicle.werePassengersInjured === 'Yes' && (
        <div className="open-form-passenger-list">
          <div className="open-form-vehicle-tabs">
            <div className="open-form-vehicle-tab-list">
              {activeVehicle.passengers.map((passenger, passengerIndex) => (
                <button
                  key={`passenger-tab-${passengerIndex + 1}`}
                  type="button"
                  className={`open-form-vehicle-tab ${passengerIndex === activePassengerIndex ? 'active' : ''}`}
                  onClick={() => setActivePassengerIndexes((current) => current.map((value, index) => (
                    index === activeVehicleIndex ? passengerIndex : value
                  )))}
                >
                  <span>Passenger {passengerIndex + 1}</span>
                </button>
              ))}
              <button
                type="button"
                className="open-form-add-vehicle-btn top"
                onClick={addPassenger}
                disabled={activeVehicle.passengers.length >= 4}
              >
                + Add Passenger
              </button>
            </div>
          </div>

          {activeVehicle.passengers.length > 1 && (
            <div className="open-form-delete-vehicle-wrap">
              <button type="button" className="open-form-delete-vehicle-btn" onClick={() => deletePassenger(activePassengerIndex)}>
                Delete Passenger
              </button>
            </div>
          )}

          {renderPassengerCard(activePassenger, activePassengerIndex)}
        </div>
      )}

      <div className="open-form-question open-form-question-compact">
        <div className="open-form-question-title"><span>Were there any pedestrians injured?</span></div>
        {renderRadioGroup('werePedestriansInjured', form.werePedestriansInjured, ADDITIONAL_INFORMATION_RADIO_OPTIONS)}
      </div>

      {form.werePedestriansInjured === 'Yes' && (
        <div className="open-form-passenger-list open-form-additional-stack">
          <div className="open-form-vehicle-tabs">
            <div className="open-form-vehicle-tab-list">
              {form.pedestrians.map((pedestrian, pedestrianIndex) => (
                <button
                  key={`pedestrian-tab-${pedestrianIndex + 1}`}
                  type="button"
                  className={`open-form-vehicle-tab ${pedestrianIndex === activePedestrianIndex ? 'active' : ''}`}
                  onClick={() => setActivePedestrianIndex(pedestrianIndex)}
                >
                  <span>Pedestrian {pedestrianIndex + 1}</span>
                </button>
              ))}
              <button
                type="button"
                className="open-form-add-vehicle-btn top"
                onClick={addPedestrian}
                disabled={form.pedestrians.length >= 2}
              >
                + Add Pedestrian
              </button>
            </div>
          </div>

          {form.pedestrians.length > 1 && (
            <div className="open-form-delete-vehicle-wrap">
              <button type="button" className="open-form-delete-vehicle-btn" onClick={() => deletePedestrian(activePedestrianIndex)}>
                Delete Pedestrian
              </button>
            </div>
          )}

          {renderPedestrianCard(form.pedestrians[activePedestrianIndex], activePedestrianIndex)}
        </div>
      )}

      <div className="open-form-question open-form-question-compact">
        <div className="open-form-question-title"><span>Were there any witnesses present?</span></div>
        {renderRadioGroup('wereWitnessesPresent', form.wereWitnessesPresent, ADDITIONAL_INFORMATION_RADIO_OPTIONS)}
      </div>

      {form.wereWitnessesPresent === 'Yes' && (
        <div className="open-form-passenger-list open-form-additional-stack">
          <div className="open-form-vehicle-tabs">
            <div className="open-form-vehicle-tab-list">
              {form.witnesses.map((witness, witnessIndex) => (
                <button
                  key={`witness-tab-${witnessIndex + 1}`}
                  type="button"
                  className={`open-form-vehicle-tab ${witnessIndex === activeWitnessIndex ? 'active' : ''}`}
                  onClick={() => setActiveWitnessIndex(witnessIndex)}
                >
                  <span>Witness {witnessIndex + 1}</span>
                </button>
              ))}
              <button
                type="button"
                className="open-form-add-vehicle-btn top"
                onClick={addWitness}
                disabled={form.witnesses.length >= 2}
              >
                + Add Witness
              </button>
            </div>
          </div>

          {form.witnesses.length > 1 && (
            <div className="open-form-delete-vehicle-wrap">
              <button type="button" className="open-form-delete-vehicle-btn" onClick={() => deleteWitness(activeWitnessIndex)}>
                Delete Witness
              </button>
            </div>
          )}

          {renderWitnessCard(form.witnesses[activeWitnessIndex], activeWitnessIndex)}
        </div>
      )}

      <div className="open-form-question open-form-question-compact">
        <div className="open-form-question-title"><span>Was personal property damaged or stolen?</span></div>
        {renderVehicleRadioGroup('wasPersonalPropertyDamaged', activeVehicle.wasPersonalPropertyDamaged, ADDITIONAL_INFORMATION_RADIO_OPTIONS)}
      </div>

      {activeVehicle.wasPersonalPropertyDamaged === 'Yes' && (
        <div className="open-form-field open-form-field-textarea">
          <label>Personal Property Damage / Theft Details</label>
          <textarea
            name="personalPropertyDescription"
            value={activeVehicle.personalPropertyDescription}
            onChange={handleVehicleChange}
            maxLength={500}
          />
          <div className="open-form-character-count">{500 - activeVehicle.personalPropertyDescription.length} characters remaining</div>
        </div>
      )}

      <div className="open-form-field open-form-field-textarea">
        <label>Additional Liability Notes</label>
        <textarea
          name="additionalInformationNotes"
          value={form.additionalInformationNotes}
          onChange={handleChange}
          maxLength={4000}
        />
        <div className="open-form-character-count">{4000 - form.additionalInformationNotes.length} characters remaining</div>
      </div>
    </div>
  );

  const renderDocumentsStep = () => (
    <div className="open-form-card">
      <div className="open-form-question">
        <div className="open-form-question-title">
          <span><span className="required mandatory-required">*</span>{' '}Upload at least one document or photo.</span>
        </div>
      </div>

      <div className="open-form-doc-section">
        <p className="open-form-doc-intro">Mandatory uploads for this claim:</p>
        <div className="open-form-doc-list-grid">
          {Array.from({ length: 2 }).map((_, columnIndex) => (
            <ul className="open-form-doc-list" key={`doc-col-${columnIndex}`}>
              {DOCUMENT_REQUIRED_UPLOADS.filter((_, itemIndex) => itemIndex % 2 === columnIndex).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ))}
        </div>
      </div>

      <div className="open-form-doc-section">
        <p className="open-form-doc-intro">Suggested uploads for this claim:</p>
        <div className="open-form-doc-list-grid">
          {Array.from({ length: 3 }).map((_, columnIndex) => (
            <ul className="open-form-doc-list" key={`suggested-doc-col-${columnIndex}`}>
              {DOCUMENT_SUGGESTIONS.filter((_, itemIndex) => itemIndex % 3 === columnIndex).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          ))}
        </div>
      </div>

      <p className="open-form-doc-note">
        Maximum size of a single file or video is 1 GB, and the total upload size is 1 GB. Currently, we cannot accept zip files through this form. You can share zip files and larger files directly with your adjustor after the report is submitted.
      </p>

      <div className={`open-form-upload-box ${errors.uploadedFiles ? 'has-error' : ''}`.trim()}>
        <label className="open-form-upload-btn">
          <IoCloudUploadOutline />
          <span>Upload Files</span>
          <input type="file" multiple onChange={handleDocumentUpload} hidden />
        </label>
        <span className="open-form-upload-hint">Or drop files</span>
      </div>
      {errors.uploadedFiles ? (
        <div className="open-form-inline-error">
          {typeof errors.uploadedFiles === 'string' ? errors.uploadedFiles : 'Keep or upload at least one file before saving this claim.'}
        </div>
      ) : null}

      {existingUploadedFiles.length > 0 && (
        <div className="open-form-uploaded-files">
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 8 }}>
            <button type="button" className="open-form-delete-vehicle-btn view-file" onClick={handleViewAllExistingFiles}>View All</button>
            <button type="button" className="open-form-delete-vehicle-btn download-file" onClick={() => window.open(`${baseURL}/claim-uploads/shuttle-bus/${submittedRow._id}/download-all`, '_blank', 'noopener,noreferrer')}>Download All as ZIP</button>
          </div>
          {existingUploadedFiles.map((file) => (
            <div key={file.id} className="open-form-uploaded-file">
              <span>{file.original_name}</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="open-form-delete-vehicle-btn view-file" onClick={() => handleViewExistingFile(file.path)}>
                  View
                </button>
                <button type="button" className="open-form-delete-vehicle-btn download-file" onClick={() => handleDownloadFile(file.path)}>
                  Download
                </button>
                <button type="button" className="open-form-delete-vehicle-btn" onClick={() => removeExistingUploadedFile(file.id)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {form.uploadedFiles.length > 0 && (
        <div className="open-form-uploaded-files">
          {form.uploadedFiles.map((file, index) => (
            <div key={`${file.name}-${index}`} className="open-form-uploaded-file">
              <span>{file.name} (New)</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="open-form-delete-vehicle-btn view-file" onClick={() => handleViewNewFile(file)}>
                  View
                </button>
                <button type="button" className="open-form-delete-vehicle-btn" onClick={() => removeUploadedFile(index)}>
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <button type="button" className="open-form-doc-toggle" onClick={toggleDocumentDefinitions}>
        <span className="open-form-doc-toggle-icon">
          {form.showDocumentDefinitions ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
        </span>
        <span>Document type definitions</span>
      </button>

      {form.showDocumentDefinitions && (
        <div className="open-form-doc-definitions">
          {DOCUMENT_DEFINITIONS.map((item) => (
            <p key={item.title}>
              <strong>{item.title}</strong>
              {' '}
              -
              {' '}
              {item.description}
            </p>
          ))}
        </div>
      )}

      <label className="open-form-checkbox open-form-checkbox-block open-form-doc-checkbox">
        <input
          type="checkbox"
          name="receiveTextConfirmation"
          checked={form.receiveTextConfirmation}
          onChange={handleChange}
        />
        <span>I would like to receive a text message confirming receipt of this claim.</span>
      </label>

      {/* <p className="open-form-fraud-warning">{FRAUD_WARNING_TEXT}</p> */}
    </div>
  );

  const renderDetailsByVehicleStep = () => (
    <div className="open-form-card open-form-vehicle-card">
      <div className="open-form-grid open-form-grid-vehicle-four">
        <div className="open-form-field">
          <label htmlFor="open-form-unit-fleet-number">
            <span className="required mandatory-required">*</span>
            {' '}
            Unit / Fleet Number
          </label>
          <input
            id="open-form-unit-fleet-number"
            name="unitFleetNumber"
            value={activeVehicle.unitFleetNumber}
            onChange={handleVehicleChange}
            className={errors.unitFleetNumber ? 'has-error' : ''}
          />
        </div>
        <div className="open-form-field">
          <label htmlFor="open-form-vehicle-vin">
            <span className="required mandatory-required">*</span>
            {' '}
            VIN
          </label>
          <input
            id="open-form-vehicle-vin"
            name="vin"
            value={activeVehicle.vin}
            onChange={handleVehicleChange}
            className={errors.vin ? 'has-error' : ''}
            minLength={16}
            maxLength={25}
          />
        </div>
        <div className="open-form-field">
          <label htmlFor="open-form-vehicle-license-plate">
            <span className="required">*</span>
            {' '}
            License Plate
          </label>
          <input
            id="open-form-vehicle-license-plate"
            name="licensePlate"
            value={activeVehicle.licensePlate}
            onChange={handleVehicleChange}
            className={errors.licensePlate ? 'has-error' : ''}
          />
        </div>
        <div className="open-form-field">
          <label htmlFor="open-form-vehicle-state-issued">
            <span className="required">*</span>
            {' '}
            Province / State
          </label>
          <select
            id="open-form-vehicle-state-issued"
            name="stateIssued"
            value={activeVehicle.stateIssued}
            onChange={handleVehicleChange}
            className={errors.stateIssued ? 'has-error' : ''}
          >
            {STATE_OPTIONS.map((option) => (
              <option key={option || 'blank'} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="open-form-grid open-form-grid-vehicle-four">
        <div className="open-form-field">
          <label htmlFor="open-form-vehicle-status">
            <span className="required">*</span>
            {' '}
            Vehicle Status
          </label>
          <select
            id="open-form-vehicle-status"
            name="vehicleStatus"
            value={activeVehicle.vehicleStatus}
            onChange={handleVehicleChange}
            className={errors.vehicleStatus ? 'has-error' : ''}
          >
            {VEHICLE_IDENTIFICATION_STATUS_OPTIONS.map((option) => (
              <option key={option || 'blank'} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        {activeVehicle.vehicleStatus === 'Other' && (
          <div className="open-form-field">
            <label htmlFor="open-form-vehicle-status-other">Vehicle Status - Other</label>
            <input
              id="open-form-vehicle-status-other"
              name="vehicleStatusOther"
              value={activeVehicle.vehicleStatusOther}
              onChange={handleVehicleChange}
              className={errors.vehicleStatusOther ? 'has-error' : ''}
              placeholder="Enter vehicle status"
            />
          </div>
        )}
        <div className="open-form-field">
          <label htmlFor="open-form-current-vehicle-location">
            <span className="required">*</span>
            {' '}
            Current Vehicle Location
          </label>
          <input
            id="open-form-current-vehicle-location"
            name="currentVehicleLocation"
            value={activeVehicle.currentVehicleLocation}
            onChange={handleVehicleChange}
            className={errors.currentVehicleLocation ? 'has-error' : ''}
          />
        </div>
        <div className="open-form-field">
          <label htmlFor="open-form-odometer-reading">
            <span className="required">*</span>
            {' '}
            Odometer Reading
          </label>
          <input
            id="open-form-odometer-reading"
            name="odometerReading"
            value={activeVehicle.odometerReading}
            onChange={handleVehicleChange}
            className={errors.odometerReading ? 'has-error' : ''}
          />
        </div>
      </div>

      <div className="open-form-grid open-form-grid-vehicle-four">
        <div className="open-form-field">
          <label htmlFor="open-form-vehicle-year">
            <span className="required">*</span>
            {' '}
            Year
          </label>
          <input
            id="open-form-vehicle-year"
            name="year"
            value={activeVehicle.year}
            onChange={handleVehicleChange}
            className={errors.year ? 'has-error' : ''}
          />
        </div>
        <div className="open-form-field">
          <label htmlFor="open-form-vehicle-make">
            <span className="required">*</span>
            {' '}
            Make
          </label>
          <input
            id="open-form-vehicle-make"
            name="make"
            value={activeVehicle.make}
            onChange={handleVehicleChange}
            className={errors.make ? 'has-error' : ''}
          />
        </div>
        <div className="open-form-field">
          <label htmlFor="open-form-vehicle-model">
            <span className="required">*</span>
            {' '}
            Model
          </label>
          <input
            id="open-form-vehicle-model"
            name="model"
            value={activeVehicle.model}
            onChange={handleVehicleChange}
            className={errors.model ? 'has-error' : ''}
          />
        </div>
        <div className="open-form-field">
          <label htmlFor="open-form-vehicle-color">
            <span className="required">*</span>
            {' '}
            Color
          </label>
          <input
            id="open-form-vehicle-color"
            name="color"
            value={activeVehicle.color}
            onChange={handleVehicleChange}
            className={errors.color ? 'has-error' : ''}
          />
        </div>
      </div>
    </div>
  );

  const renderPlaceholderStep = () => (
    <div className="open-form-card open-form-placeholder">
      <h3>{activeLabel}</h3>
      <p>{STEP_PLACEHOLDER_COPY[activeStep]}</p>
      <span>Share the next set of fields and I can plug them into this step.</span>
    </div>
  );

  if (!canView) {
    return <AccessDeniedState message={accessDeniedMessage} />;
  }

  if (!submittedRow?._id) {
    return null;
  }

  return (
    <>
    <div className={`open-form-page ${onCancel ? 'open-form-page-embedded' : ''}`.trim()}>
      {!canEdit ? (
        <div className="ra-error" style={{ marginBottom: 16 }}>
          You have view access only. Edit access is not enabled for your role.
        </div>
      ) : null}
      {/* <div className="open-form-header">
        <div>
          <h1>Auto</h1>
        </div>
        <button type="button" className="open-form-cancel-btn" onClick={() => navigate('/close-form')}>
          Cancel
        </button>
      </div> */}

      <div className="open-form-steps" aria-label="Open form steps">
        {STEP_ITEMS.map((step, index) => {
          const stateClass = index === activeStep ? 'active' : index < activeStep ? 'done' : '';

          return (
            <button
              key={step}
              type="button"
              className={`open-form-step ${stateClass}`.trim()}
              onClick={() => {
                if (index <= activeStep) {
                  setActiveStep(index);
                }
              }}
            >
              <span>{step}</span>
            </button>
          );
        })}
      </div>

      <div className="open-form-body">
        {activeStep === 0
          ? renderStartStep()
          : activeStep === 1
            ? renderRoutesInsuredStep()
          : activeStep === 2
            ? renderAccidentOverviewStep()
            : activeStep === 3
            ? renderDetailsByVehicleStep()
            : activeStep === 4
            ? renderIncidentSpecificsStep()
            : activeStep === 5
            ? renderAdditionalInformationStep()
            : activeStep === 6
              ? renderDocumentsStep()
              : renderPlaceholderStep()}
      </div>

      <div className="open-form-footer">
        <button
          type="button"
          className="open-form-nav-btn secondary"
          onClick={onCancel || (() => navigate(submittedFormsPath))}
        >
          Cancel
        </button>
        <button
          type="button"
          className="open-form-nav-btn secondary"
          onClick={handleBack}
          disabled={activeStep === 0}
        >
          <IoChevronBackOutline />
          Back
        </button>

        {(activeStep !== STEP_ITEMS.length - 1 || canEdit) && (
          <>
          {activeStep === STEP_ITEMS.length - 1 && canUpdate ? (
            <button
              disabled={submitting}
              type="button"
              className="open-form-nav-btn primary"
              onClick={() => handleSubmit(false)}
            >
              {submitting ? 'Saving...' : 'Update'}
            </button>
          ) : null}
          {(activeStep !== STEP_ITEMS.length - 1 || canProcess) ? <button
            disabled={submitting}
            type={activeStep === STEP_ITEMS.length - 1 ? 'submit' : 'button'}
            className="open-form-nav-btn primary"
            onClick={activeStep === STEP_ITEMS.length - 1 ? () => handleSubmit(promoteToProcessingOnSubmit) : handleNext}
          >
            {activeStep === STEP_ITEMS.length - 1 ? (
              <>{submitting ? 'Saving...' : finalSubmitLabel}</>
            ) : (
              <>Next<IoChevronForwardOutline /></>
            )}
          </button> : null}
          </>
        )}
      </div>
    </div>
    {impactEditor && (
      <div className="open-form-impact-modal" role="dialog" aria-modal="true" aria-label="Point of impact details">
        <div className="open-form-impact-popover">
          <div className="open-form-field">
            <label htmlFor="edit-form-impact-details">Details</label>
            <textarea
              id="edit-form-impact-details"
              name="details"
              value={impactEditor.details}
              onChange={handleImpactEditorChange}
              rows={4}
              placeholder={impactEditor.label}
              autoFocus
            />
          </div>

          <div className="open-form-impact-actions">
            <button type="button" className="open-form-nav-btn secondary" onClick={closePointOfImpactEditor}>
              Cancel
            </button>
            <button type="button" className="open-form-nav-btn primary" onClick={savePointOfImpact}>
              Save
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
